import { useState, useEffect } from 'react'
import { useAuth } from '../../../hooks/useAuth'
import { identityService } from '../../../shared/api'
import { Button } from '../../../components'
import { useToast } from '../../../components/ToastProvider'
import { useTenantBranding } from '../../../contexts/TenantContext.jsx'
import { DEFAULT_IDLE_MINUTES, DEFAULT_ABSOLUTE_HOURS } from '../../../shared/api/sessionPolicy'
import { Clock, Info } from 'lucide-react'

/**
 * How long somebody stays signed in here.
 *
 * WHY THIS IS A SETTING AND NOT A CONSTANT. It used to be twenty-five minutes, compiled into the
 * bundle, the same for every company. Two construction companies answer this differently and both
 * answers are right: a firm running shared laptops in an open site trailer wants a short leash; an
 * office of assigned machines behind a locked door does not, and being signed out every half hour
 * while pricing a bid is how people learn to hate a tool.
 *
 * WHAT THE TWO NUMBERS MEAN, and why there are two. They fail in different ways.
 *   - IDLE is "you have done nothing for this long". It is the one people notice.
 *   - MAXIMUM is "this session has simply been alive this long", however busy it has been. It is
 *     what makes sure a laptop closed at six is not still signed in the next morning, and it is the
 *     one that keeps working if the idle clock is ever wrong again.
 *
 * BOTH ARE BLANK UNTIL SOMEBODY SETS THEM, and blank is a real answer — it means "use the product
 * default", not zero. Clearing a field puts a company back on the default rather than switching the
 * protection off; there is no way from this screen to have no timeout at all, deliberately.
 *
 * WHAT EACH ONE ACTUALLY BUYS, which is not the same for the two of them, and the note at the foot
 * of the card says so because a person setting "10 minutes" here is entitled to know what they
 * have and have not just bought.
 *   - IDLE is enforced in the BROWSER only. It decides when the app signs somebody out on their own
 *     screen. It does not shorten the life of the credential sitting in that browser.
 *   - MAXIMUM is enforced on the SERVER too. `/auth/refresh` refuses to renew a session past this,
 *     so the credential itself stops working — including one copied onto another machine. See
 *     src/modules/identity/backend/services/session_policy.py.
 * Do not flatten that distinction back into one sentence. It was two different promises before
 * anyone wrote them down, and telling a customer the idle box protects a stolen laptop would be
 * the kind of untrue that only surfaces after something has gone wrong.
 */
// The tenant this page is editing. Same helper, same reasons, as ReplyToSettings: `user.tenant_id`
// does not exist (the login response declares no tenant id — it lives only inside the signed JWT),
// and `'master'` is the platform-admin host, which is not a company and has nothing to edit here.
function useEditableTenantId() {
  const branding = useTenantBranding()
  const id = branding?.tenantId
  return id && id !== 'master' ? id : null
}

/** '' for a blank field, or the number. Anything else — letters, a stray minus — reads as blank. */
function asText(value) {
  return typeof value === 'number' && Number.isFinite(value) ? String(value) : ''
}

export default function SessionPolicySettings() {
  const { isAdmin } = useAuth()
  const toast = useToast()
  const branding = useTenantBranding()
  const tenantId = useEditableTenantId()

  const [idle, setIdle] = useState('')
  const [absolute, setAbsolute] = useState('')
  const [savedIdle, setSavedIdle] = useState('')
  const [savedAbsolute, setSavedAbsolute] = useState('')
  const [saving, setSaving] = useState(false)
  const [loadError, setLoadError] = useState(null)

  const canEdit = isAdmin && isAdmin()

  useEffect(() => {
    if (!tenantId) {
      // NOT a silent empty card. Rendering nothing reads as "this is switched off", which is a
      // different and more misleading statement than "we cannot tell whose settings these are".
      setLoadError('These settings belong to one company. Open this page on that company\'s own address.')
      return
    }
    setLoadError(null)
    // Read from the branding payload the app already resolved by host at first paint. No second
    // request, and — more importantly — the same source the session clock itself reads, so this
    // screen cannot show a number the app is not actually using.
    const i = asText(branding?.sessionIdleMinutes)
    const a = asText(branding?.sessionAbsoluteHours)
    setIdle(i); setSavedIdle(i)
    setAbsolute(a); setSavedAbsolute(a)
  }, [tenantId, branding?.sessionIdleMinutes, branding?.sessionAbsoluteHours])

  const dirty = idle.trim() !== savedIdle.trim() || absolute.trim() !== savedAbsolute.trim()

  const parse = (text, min, max, label) => {
    const t = text.trim()
    if (t === '') return { ok: true, value: null }        // blank = back to the default
    if (!/^\d+$/.test(t)) return { ok: false, error: `${label} must be a whole number, or blank.` }
    const n = parseInt(t, 10)
    if (n < min || n > max) return { ok: false, error: `${label} must be between ${min} and ${max}.` }
    return { ok: true, value: n }
  }

  const handleSave = async () => {
    const i = parse(idle, 5, 24 * 60, 'The idle timeout')
    const a = parse(absolute, 1, 24 * 7, 'The maximum session length')
    if (!i.ok) { toast.error(i.error); return }
    if (!a.ok) { toast.error(a.error); return }

    setSaving(true)
    try {
      // Both keys sent explicitly, always, so the server can tell "clear it" from "leave it alone"
      // — the branding update reads `model_fields_set`, and an omitted key means untouched.
      await identityService.updateBranding(tenantId, {
        session_idle_minutes: i.value,
        session_absolute_hours: a.value,
      })
      setSavedIdle(idle.trim()); setSavedAbsolute(absolute.trim())
      // Re-resolve branding so the running app adopts the new window immediately rather than at the
      // next reload — this is the payload the session clock reads.
      if (typeof branding?.refresh === 'function') branding.refresh()
      toast.success('Session policy saved. It applies from each person\'s next page load.')
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save the session policy.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <Clock className="h-5 w-5 text-gray-500" />
        <h3 className="text-lg font-semibold text-gray-900">Staying signed in</h3>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        How long somebody at this company stays signed in. Leave a box empty to use our default.
      </p>

      {loadError ? (
        <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
          <Info className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{loadError}</span>
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="block text-sm font-medium text-gray-700">Sign out after doing nothing for</span>
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  className="w-28 rounded-md border border-gray-300 px-3 py-2 text-sm"
                  placeholder={String(DEFAULT_IDLE_MINUTES)}
                  value={idle}
                  disabled={!canEdit || saving}
                  onChange={(e) => setIdle(e.target.value)}
                />
                <span className="text-sm text-gray-600">minutes</span>
              </div>
              <span className="mt-1 block text-xs text-gray-500">
                Empty means our default, {DEFAULT_IDLE_MINUTES} minutes. Anything from 5 minutes to 24 hours.
                People get a two-minute warning first, with a button to stay.
              </span>
            </label>

            <label className="block">
              <span className="block text-sm font-medium text-gray-700">Sign out after being signed in for</span>
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  className="w-28 rounded-md border border-gray-300 px-3 py-2 text-sm"
                  placeholder={String(DEFAULT_ABSOLUTE_HOURS)}
                  value={absolute}
                  disabled={!canEdit || saving}
                  onChange={(e) => setAbsolute(e.target.value)}
                />
                <span className="text-sm text-gray-600">hours</span>
              </div>
              <span className="mt-1 block text-xs text-gray-500">
                Empty means our default, {DEFAULT_ABSOLUTE_HOURS} hours — roughly once a working day.
                This one applies however busy the person has been, and it is the one enforced on our
                servers as well.
              </span>
            </label>
          </div>

          <div className="mt-4 flex items-start gap-2 rounded-md bg-gray-50 border border-gray-200 p-3 text-xs text-gray-600">
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              The two boxes do not buy quite the same thing. The idle timeout is when the app signs
              somebody out on the screen in front of them. The maximum session length is also
              enforced on our servers, so once it passes, the sign-in stops working everywhere —
              including on a laptop that has been lost or a browser session someone has copied.
            </span>
          </div>

          {canEdit && (
            <div className="mt-4">
              <Button variant="primary" onClick={handleSave} disabled={!dirty || saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
